import { useMutation, useQuery, useQueryClient } from "react-query";
import { queryKeys } from "./queryKeys";
import { axiosPath } from "@src/api/axiosPath";
import { axiosInstance } from "@src/api/axiosConfig";
import { asyncCall } from "@src/api/asyncCall";
import { BaseExpertDetail, UpdateExpertDto, DetailExpertProps } from "@src/interfaces/Expert.interface";

const fetchData = async (expertNo: number) => {
    const response = await axiosInstance.get(`${axiosPath.experts}/${expertNo}`);
    if (!response.status) {
        throw new Error("Problem fetching data");
    }
    const responseData = response.data;
    const originData: BaseExpertDetail = responseData.data.data[0];
    return parse(originData);
};

const parse = (originData: BaseExpertDetail): DetailExpertProps => {
    const {
        uname,
        expert_no,
        expert_status,
        scope,
        category,
        career,
        introduce,
        profile_imgurl,
        biz_registration_imgurl,
        agent_licence_imgurl,
        buyrequest_agent_imgurl,
        biz_registration_number,
        biz_name,
        boss_name,
        registration_number,
        tel,
        region,
        agent_no,
        confirm_no,
    } = originData;

    return {
        expertInfoData: {
            imgUrl: profile_imgurl,
            expertStatus: expert_status,
            adStatus: 0,
            counselType: scope !== "" ? scope.split(",") : [],
            counselField: category !== "" ? category?.split(",") : [],
            careers: career,
            introduction: introduce,
            edit: false,
            userName: uname,
        },
        officeData: {
            no: agent_no,
            officeName: biz_name,
            businessName: boss_name,
            registeredNumber: registration_number,
            location: region,
            phoneNumber: tel,
            bizNumber: biz_registration_number,
            edit: false,
        },
        documentData: {
            businessLicenseUrl: biz_registration_imgurl,
            officeRegisterUrl: agent_licence_imgurl,
            deputiesLicenseUrl: buyrequest_agent_imgurl,
            edit: false,
        },
        edit: false,
        confirmNo: confirm_no,
    };
};
// const useExpertDetailFetchData = (expertNo: number) => {
//     return useQuery<DetailExpertProps, Error>([queryKeys.EXPERT_DETAIL_DATA, expertNo], () => fetchData(expertNo), {
//         // initialData: initialExpertDetailData,
//     });
// };

// const useUpdateExpertData = (expertNo: number) => {
//     const updateExpert = async (params: UpdateExpertDto) => {
//         const promise = await axiosInstance.patch(`${axiosPath.experts}/${expertNo}`, params);
//         return asyncCall(promise, "Common");
//     };

//     return useMutation(updateExpert);
// };

// export { useExpertDetailFetchData, useUpdateExpertData };

// 2025-06-18 추가

export const EMPTY_EXPERT_OFFICE: ExpertProfileDetail["office"] = {
    name: "",
    ceo: "",
    registrationNumber: "",
    type: "",
    phone: "",
    address: "",
    pnu: "",
};

export type ExpertProfileDetailRaw = Omit<ExpertProfileDetail, "office" | "hashTags"> & {
    office?: ExpertProfileDetail["office"];
    hashTags?: string[];
};

export function buildExpertUpdateCareerString(
    detail: Pick<ExpertProfileDetail, "auctionAgentCareer" | "consultingCareer" | "companionCareer" | "agentCareer">,
): string {
    const labelByField = {
        auctionAgentCareer: "경매대행",
        consultingCareer: "매물컨설팅",
        companionCareer: "임장동행",
        agentCareer: "중개",
    } as const;

    const map: Record<string, number> = {};
    (Object.keys(labelByField) as (keyof typeof labelByField)[]).forEach((field) => {
        const v = detail[field];
        if (typeof v === "number" && v !== 0 && Number.isFinite(v)) {
            map[labelByField[field]] = v;
        }
    });
    return Object.keys(map).length ? JSON.stringify(map) : "";
}

export const normalizeExpertProfileDetail = (data: ExpertProfileDetailRaw): ExpertProfileDetail => {
    const hashTags = Array.isArray(data.hashTags) ? data.hashTags : [];
    let counselingCategories = Array.isArray(data.counselingCategories) ? [...data.counselingCategories] : [];
    if (counselingCategories.length === 0 && hashTags.length > 0) {
        counselingCategories = hashTags.map((tag) => {
            const t = String(tag).trim();
            return t.startsWith("#") ? t : `#${t}`;
        });
    }
    return {
        ...(data as ExpertProfileDetail),
        hashTags,
        counselingCategories,
        office: { ...EMPTY_EXPERT_OFFICE, ...(data.office ?? {}) },
    };
};

const fetchExpertProfileDetail = async (no: number, numType: "expertNo" | "userNo") => {
    const response = await axiosInstance.get<ExpertProfileDetailRaw>(`${axiosPath.expertProfile}`, {
        params: {
            [numType]: no,
        },
    });
    return normalizeExpertProfileDetail(response.data);
};
export const useExpertProfileDetail = (no: number, numType: "expertNo" | "userNo") => {
    return useQuery([queryKeys.EXPERT_DETAIL_DATA, no], () => fetchExpertProfileDetail(no, numType));
};

export type ExpertProfileDetail = {
    expertNo: number;
    phone: string;
    name: string;
    auctionAgentJoinStatus: string;
    typeStr: string;
    officeName: string;
    hashTags: string[];
    profileImageUrl: string;
    intro: string;
    counselingCategories: string[];
    reviewCount: number;
    career: string;
    qualification: string;
    office: {
        name: string;
        ceo: string;
        registrationNumber: string;
        type: string;
        phone: string;
        address: string;
        pnu: string;
    };
    bankName: string;
    bankAccount: string;
    auctionAgentRegistrationImage: string;
    auctionAgentJoinRequestDate: string;
    auctionAgentCareer: number;
    consultingCareer: number;
    companionCareer: number;
    agentCareer: number;
};

const approveExperts = async (expertUserNoList: number[]) => {
    const response = await axiosInstance.patch(`${axiosPath.expertApproval}`, {
        expertUserNoList,
    });
    return response.data;
};

const rejectExpert = async (userExpertNo: number) => {
    const response = await axiosInstance.patch(`${axiosPath.expertReject}/${userExpertNo}`);
    return response.data;
};

export const useApproveExperts = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (expertUserNoList: number[]) => approveExperts(expertUserNoList),
        onSuccess: () => {
            queryClient.invalidateQueries([queryKeys.EXPERT_DETAIL_DATA]);
        },
    });
};

export const useRejectExpert = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (userExpertNo: number) => rejectExpert(userExpertNo),
        onSuccess: () => {
            queryClient.invalidateQueries([queryKeys.EXPERT_DETAIL_DATA]);
        },
    });
};

export type UpdateExpertProfileDto = {
    expertNo: number;
    phone: string;
    profile: string;
    officeInfo: {
        name: string;
        ceo: string;
        phone: string;
        address: string;
        registrationNumber?: string;
        type?: string;
        pnu?: string;
    };
    counselingCategories: string[];
    isAuctionAgent: boolean;
    bankName: string;
    bankAccount: string;
    auctionAgentCertFileUrl?: string;
    introduction?: string;
    career?: string;
    qualification?: string;
};

const updateExpertProfile = async (body: UpdateExpertProfileDto) => {
    const response = await axiosInstance.put(`${axiosPath.expertProfileUpdate}`, body);
    return response.data;
};

export const useUpdateExpertProfile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: UpdateExpertProfileDto) => updateExpertProfile(body),
        onSuccess: () => {
            queryClient.invalidateQueries([queryKeys.EXPERT_DETAIL_DATA]);
        },
    });
};
